import { useCallback } from 'react';
import useSWRInfinite from 'swr/infinite';

import type { BasePaginatedResponse } from 'models/api';
import type { JSONValue } from 'types';
import { fetchData } from 'utils/fetch';
import { is } from 'utils/helpers';
import { createUrl } from 'utils/url';

const bodyStartDelimiter = '<<BODY_';
const bodyEndDelimiter = '_BODY>>';
const bodyKeyRegExp = new RegExp(
	String.raw`${bodyStartDelimiter}[\s\S]*${bodyEndDelimiter}`,
);

export default function usePagedData<
	R extends BasePaginatedResponse,
	K extends Exclude<keyof R, keyof BasePaginatedResponse> = Exclude<
		keyof R,
		keyof BasePaginatedResponse
	>,
>({
	baseUrl,
	body,
	isActive = true,
	itemsKey,
	keepPreviousData = false,
	method,
	pageSize,
	queryParams,
}: {
	baseUrl: string | null;
	body?: JSONValue;
	isActive?: boolean;
	itemsKey: K;
	keepPreviousData?: boolean;
	method?: 'POST';
	pageSize?: number;
	queryParams?: Record<string, string>;
}) {
	const getKey = (pageIndex: number, previousPageData: R | undefined) => {
		if (
			!baseUrl ||
			!isActive ||
			(previousPageData && !previousPageData.hasNextPage)
		) {
			return null;
		}
		const url = createUrl(baseUrl, {
			...queryParams,
			pageSize,
			offset: pageIndex === 0 ? null : previousPageData?.nextPageOffset,
		});
		const bodyKey = `${bodyStartDelimiter}${JSON.stringify(body)}${bodyEndDelimiter}`;
		return `${url}${bodyKey}`;
	};

	const { data, error, isLoading, setSize, size } = useSWRInfinite(
		getKey,
		(key) => fetchData<R>(key.replace(bodyKeyRegExp, ''), { body, method }),
		{
			keepPreviousData,
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			revalidateFirstPage: false,
		},
	);

	const loadMore = useCallback(() => setSize(size + 1), [setSize, size]);
	const lastResponse = data?.at(-1);
	const {
		hasNextPage,
		// Items data is processed below.
		// oxlint-disable-next-line typescript/no-unused-vars
		[itemsKey]: excluded,
		// Next page offset is not needed outside the hook.
		// oxlint-disable-next-line typescript/no-unused-vars
		nextPageOffset,
		...rest
	} = lastResponse ?? ({} as Partial<R>);

	return {
		items: data
			?.filter(is.truthy)
			.map((response) => response[itemsKey])
			.filter(is.truthy)
			.flat(),
		isLoading,
		// swr-infinite doesn't have a built-in way to say if we're loading when the first load is done
		isLoadingMore:
			isLoading || (size > 0 && data && data[size - 1] === undefined),
		error,
		loadMore,
		hasNextPage: Boolean(hasNextPage),
		...rest,
	};
}
